<template>
  <div class="auth-promo-panel">
    <div
      v-for="(slide, index) in slides"
      :key="index"
      class="promo-slide"
      :class="{ 'is-active': index === activeIndex }"
    >
      <template v-if="loadedSlides.indexOf(index) !== -1">
        <img :src="slide.bgImage" class="promo-slide-bg" :alt="slide.alt" />
        <div class="promo-slide-overlay"></div>
        <div class="promo-slide-features">
          <img :src="slide.featureImage1" class="promo-feature promo-feature-1" alt="" />
          <img :src="slide.featureImage2" class="promo-feature promo-feature-2" alt="" />
        </div>
      </template>
    </div>

    <div class="promo-text-gradient"></div>
    <div class="promo-copy">
      <transition name="promo-fade" mode="out-in">
        <div :key="activeIndex">
          <h3 class="promo-title" v-html="formatTitle(slides[activeIndex].heading)"></h3>
          <p class="promo-description">{{ slides[activeIndex].description }}</p>
        </div>
      </transition>
      <div class="promo-dots">
        <button
          v-for="(slide, index) in slides"
          :key="index"
          type="button"
          class="promo-dot"
          :class="{ 'is-active': index === activeIndex }"
          :aria-label="'Show slide ' + (index + 1)"
          @click="goTo(index)"
        ></button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AuthPromoPanel",
  data() {
    return {
      activeIndex: 0,
      // Only slide 0 loads with the page; the rest are preloaded after
      // first paint so they never block the auth form
      loadedSlides: [0],
      rotateTimer: null,
      preloadTimer: null,
      slides: [
        {
          bgImage: "/img/onboarding-slide-bg-1.jpg",
          featureImage1: "/img/onboarding-slide-feature-2.png",
          featureImage2: "/img/onboarding-slide-feature-1.png",
          heading: "Your Digital Files<br/> Approved {2X} Faster",
          description:
            "Ashore gets files approved twice as fast through automatic reminders, automated workflows and beautiful, white-labeled review screens that anybody can use!",
          alt: "Promo Image 1",
        },
        {
          bgImage: "/img/onboarding-slide-bg-2.jpg",
          featureImage1: "/img/onboarding-slide-feature-4.png",
          featureImage2: "/img/onboarding-slide-feature-3.png",
          heading: "Collaborate on Your <br/> Proofs in {Realtime}",
          description:
            "Collaborating on files has never been easier. Join your approvers, watch them navigate through your proofs. Spotlight them to see their exact view of a proof.",
          alt: "Promo Image 2",
        },
        {
          bgImage: "/img/onboarding-slide-bg-3.jpg",
          featureImage1: "/img/onboarding-slide-feature-6.png",
          featureImage2: "/img/onboarding-slide-feature-5.png",
          heading: "{Automate} Your <br/> Approval Process",
          description:
            "Create and manage approval stages for your proofs. Every proof has a complete newsfeed of every version, comment, note and action.",
          alt: "Promo Image 3",
        },
      ],
    };
  },
  mounted() {
    this.preloadTimer = setTimeout(() => {
      this.loadedSlides = this.slides.map((slide, index) => index);
    }, 1200);

    const reducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reducedMotion) {
      this.startRotation();
    }
  },
  beforeDestroy() {
    this.stopRotation();
    if (this.preloadTimer) {
      clearTimeout(this.preloadTimer);
    }
  },
  methods: {
    formatTitle(title) {
      return title.replace(/\{(.+?)\}/g, `<span class="promo-highlight">$1</span>`);
    },
    startRotation() {
      this.stopRotation();
      this.rotateTimer = setInterval(() => {
        this.activeIndex = (this.activeIndex + 1) % this.slides.length;
      }, 6000);
    },
    stopRotation() {
      if (this.rotateTimer) {
        clearInterval(this.rotateTimer);
        this.rotateTimer = null;
      }
    },
    goTo(index) {
      if (this.loadedSlides.indexOf(index) === -1) {
        this.loadedSlides = this.loadedSlides.concat(index);
      }
      this.activeIndex = index;
      this.startRotation();
    },
  },
};
</script>

<style scoped>
.auth-promo-panel {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #0b1220;
}

.promo-slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 600ms ease;
  pointer-events: none;
}

.promo-slide.is-active {
  opacity: 1;
}

.promo-slide-bg {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.promo-slide-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.7) 100%);
}

.promo-slide-features {
  position: absolute;
  inset: 0;
}

.promo-feature {
  position: absolute;
  width: 340px;
  max-width: 45%;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.promo-feature-1 {
  top: 22%;
  left: 18%;
}

.promo-feature-2 {
  top: 34%;
  left: 38%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.promo-text-gradient {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 420px;
  background: linear-gradient(to top, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0.9), transparent);
  pointer-events: none;
}

.promo-copy {
  position: absolute;
  bottom: 32px;
  left: 0;
  padding: 0 48px;
  max-width: 80%;
}

.promo-title {
  font-size: 32px;
  line-height: 1.25;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #fff;
  margin-bottom: 10px;
  text-align: left;
}

.promo-title ::v-deep .promo-highlight {
  background-color: #019256;
  display: inline-block;
  padding: 0 6px;
  border-radius: 4px;
  color: #fff;
}

.promo-description {
  font-size: 14px;
  line-height: 1.6;
  color: #919eab;
  text-align: left;
  margin-bottom: 20px;
}

.promo-dots {
  display: flex;
  gap: 8px;
}

.promo-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  border: 0;
  padding: 0;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.25);
  transition: background-color 120ms ease, width 120ms ease;
}

.promo-dot.is-active {
  background: #019256;
  width: 22px;
}

.promo-dot:focus-visible {
  outline: 2px solid #019256;
  outline-offset: 2px;
}

.promo-fade-enter-active,
.promo-fade-leave-active {
  transition: opacity 260ms ease, transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.promo-fade-enter {
  opacity: 0;
  transform: translateY(8px);
}

.promo-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (prefers-reduced-motion: reduce) {
  .promo-slide {
    transition: none;
  }
  .promo-fade-enter-active,
  .promo-fade-leave-active {
    transition: none;
  }
}
</style>
